/**
 * `order_card` widget — a compact summary of a task / order / service record.
 *
 * The whole card is tappable: a finished order opens its service record,
 * anything else opens the in-progress task. Navigation reuses the AI
 * `client_action` navigate path (`useFireClientAction`), which resolves the
 * service-record id from the orderNo and closes the AI panel.
 */

import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { AIClientActionNavigate } from '@euler/model/ai';
import React, { memo, useMemo } from 'react';
import { Text, TouchableOpacity, View } from 'react-native';
import { useTheme } from 'styled-components/native';
import { useFireClientAction } from '../../ai/useFireClientAction';
import {
  OrderCardFlow,
  OrderCardPayload,
  OrderCardStatus,
  WorkflowStageStatus,
} from './payloads';
import { useWidgetStyles } from './styles';

const STAGE_COLOR: Record<WorkflowStageStatus, string> = {
  completed: '#22C55E',
  in_progress: '#3B82F6',
  pending: '#CBD5E1',
};

export const OrderCardWidget = memo(({ data }: { data: OrderCardPayload }) => {
  const { rpx } = useResponsiveContext();
  const theme = useTheme();
  const s = useWidgetStyles();
  const fire = useFireClientAction();

  const status = statusMeta(data.status, theme.link);
  const finished = data.status === 'finished';
  const showBasics = data.showVehicleBasics !== false;

  const action = useMemo(
    () => navigateActionFor(data, finished),
    [data, finished],
  );

  const vehicleTitle =
    data.vehicle?.name ||
    joinTruthy([data.vehicle?.brandName, data.vehicle?.modelName], ' ') ||
    data.licensePlateNo ||
    '车辆';
  // When the basics are hidden the vehicle is already known from the answer,
  // so lead with the visit instead of repeating the plate/name.
  const title = showBasics ? vehicleTitle : finished ? '服务记录' : '工单';

  const body = (
    <View style={[s.card, { borderLeftColor: status.color }]}>
      <View style={s.row}>
        <Text style={s.title} numberOfLines={1}>
          {title}
        </Text>
        <View style={[s.statusBadge, { backgroundColor: status.color + '20' }]}>
          <Text style={[s.statusText, { color: status.color }]}>
            {status.label}
          </Text>
        </View>
      </View>

      {showBasics ? (
        <>
          {data.licensePlateNo ||
          data.mileage != null ||
          data.vehicle?.fuelType ? (
            <Text style={s.meta}>
              {joinTruthy(
                [
                  data.licensePlateNo,
                  data.mileage != null ? `${data.mileage} km` : null,
                  data.vehicle?.fuelType,
                  data.vehicle?.age != null
                    ? `车龄${data.vehicle.age}年`
                    : null,
                ],
                ' · ',
              )}
            </Text>
          ) : null}
          {data.vehicle?.modelYear ? (
            <Text style={s.meta}>{`${data.vehicle.modelYear}款`}</Text>
          ) : null}
        </>
      ) : null}

      {data.orderDate || data.finishedDate ? (
        <Text style={s.meta}>
          进店: {data.orderDate ?? '—'}
          {finished && data.finishedDate
            ? `  →  完工: ${data.finishedDate}`
            : ''}
        </Text>
      ) : null}

      {data.serviceType || data.serviceAgentName ? (
        <Text style={s.meta}>
          {joinTruthy(
            [
              data.serviceType ? `服务类型: ${data.serviceType}` : null,
              data.serviceAgentName
                ? `服务顾问: ${data.serviceAgentName}`
                : null,
            ],
            ' | ',
          )}
        </Text>
      ) : null}

      {data.customerName || data.subscribed ? (
        <Text style={s.meta}>
          {data.customerName ? `客户: ${data.customerName}` : ''}
          {data.subscribed ? '  · 已订阅' : ''}
        </Text>
      ) : null}

      {data.tags && data.tags.length > 0 ? (
        <View style={chipRow(rpx)}>
          {data.tags.map((t, i) => (
            <View key={i} style={tagChip(rpx)}>
              <Text style={tagChipText(rpx)}>{t}</Text>
            </View>
          ))}
        </View>
      ) : null}

      {data.flows && data.flows.length > 0 ? (
        <View style={chipRow(rpx)}>
          {data.flows.map((f, i) => (
            <FlowChip key={i} flow={f} rpx={rpx} />
          ))}
        </View>
      ) : null}
    </View>
  );

  if (action == null) return body;
  return (
    <TouchableOpacity activeOpacity={0.7} onPress={() => fire(action)}>
      {body}
    </TouchableOpacity>
  );
});

const FlowChip = memo(
  ({ flow, rpx }: { flow: OrderCardFlow; rpx: (n: number) => number }) => {
    const color = STAGE_COLOR[flow.status] ?? STAGE_COLOR.pending;
    return (
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          gap: rpx(4),
          paddingHorizontal: rpx(8),
          paddingVertical: rpx(3),
          borderRadius: rpx(10),
          backgroundColor: color + '1A',
        }}
      >
        <View
          style={{
            width: rpx(6),
            height: rpx(6),
            borderRadius: rpx(3),
            backgroundColor: color,
          }}
        />
        <Text style={{ fontSize: rpx(11), color: '#475569' }}>{flow.name}</Text>
      </View>
    );
  },
);

// ── helpers ─────────────────────────────────────────────────────────

function statusMeta(
  status: OrderCardStatus,
  linkColor: string,
): { label: string; color: string } {
  switch (status) {
    case 'finished':
      return { label: '已完工', color: '#22C55E' };
    case 'inprogress':
      return { label: '进行中', color: linkColor };
    case 'cancelled':
      return { label: '已取消', color: '#94A3B8' };
    case 'pending':
      return { label: '待处理', color: '#F59E0B' };
  }
}

/**
 * Build the navigate action for a tap, or null when the card can't resolve a
 * destination (a finished order needs its orderNo; anything else its taskNo).
 */
function navigateActionFor(
  data: OrderCardPayload,
  finished: boolean,
): AIClientActionNavigate | null {
  if (finished) {
    if (!data.orderNo) return null;
    return {
      action: 'navigate',
      page: 'service_record',
      params: {
        taskNo: data.taskNo ?? '',
        orderNo: data.orderNo,
        ...(data.serviceRecordNo
          ? { serviceRecordNo: data.serviceRecordNo }
          : {}),
      },
      label: '查看服务记录',
    };
  }
  if (!data.taskNo) return null;
  return {
    action: 'navigate',
    page: 'task_detail',
    params: { taskNo: data.taskNo },
    label: '打开工单',
  };
}

function joinTruthy(parts: (string | null | undefined)[], sep: string): string {
  return parts.filter(Boolean).join(sep);
}

const chipRow = (rpx: (n: number) => number) => ({
  flexDirection: 'row' as const,
  flexWrap: 'wrap' as const,
  gap: rpx(6),
  marginTop: rpx(6),
});

const tagChip = (rpx: (n: number) => number) => ({
  paddingHorizontal: rpx(8),
  paddingVertical: rpx(3),
  borderRadius: rpx(10),
  backgroundColor: '#EEF2FF',
});

const tagChipText = (rpx: (n: number) => number) => ({
  fontSize: rpx(11),
  color: '#4F46E5',
});
